<template>
  <div class="poster-editor">
    <header class="header">
      <div class="header-content">
        <div class="logo-container">
          <img :src="logo" alt="Joglo Prints Logo" class="header-logo" />
        </div>
        <div class="header-text">
          <h1>Poster Creator Studio</h1>
          <p class="header-subtitle">Create Professional Event Posters</p>
        </div>
      </div>
    </header>

    <div class="container">
      <div class="controls">
        <div class="user-inputs">
          <div class="input-group">
            <label for="userName">Your Name:</label>
            <input
              type="text"
              id="userName"
              v-model="userName"
              placeholder="Enter your name"
              class="user-input"
            />
          </div>
        </div>

        <div class="action-buttons">
          <label class="upload-btn" for="imageUpload">
            <input
              type="file"
              id="imageUpload"
              ref="fileInput"
              @change="handleImageUpload"
              accept="image/png,image/jpeg,image/jpg"
              hidden
            />
            📁 Upload Image
          </label>

          <button
            class="download-btn"
            @click="downloadPoster"
            :disabled="!canDownload"
          >
            💾 Download Poster
          </button>

          <button
            class="reset-btn"
            @click="resetImagePosition"
            :disabled="!uploadedImage"
            v-if="uploadedImage"
          >
            🔄 Reset Position
          </button>

          <button
            class="removebg-btn"
            @click="removeBg"
            :disabled="!uploadedImage || isRemovingBg"
            v-if="uploadedImage"
          >
            <span v-if="isRemovingBg">⏳ Removing...</span>
            <span v-else>✂ Remove Background</span>
          </button>
        </div>
      </div>

      <div class="poster-preview" ref="posterPreview">
        <div class="poster-container" ref="posterContainer">
          <!-- Poster template as background -->
          <img
            :src="posterTemplate"
            alt="Poster Template"
            class="poster-template"
            @load="onPosterLoaded"
          />

          <!-- Interactive image area overlay for uploaded image -->
          <div
            v-if="uploadedImage"
            class="image-interaction-area"
            :style="imageInteractionStyle"
            @mousedown="startDrag"
            @touchstart="startTouch"
            @wheel.prevent.stop="handleWheel"
            @touchmove.prevent.stop="handleTouch"
          >
            <div class="uploaded-image-container" :style="imageContainerStyle">
              <img
                :src="uploadedImage"
                class="uploaded-image"
                :style="uploadedImageStyle"
                @load="onImageLoaded"
                draggable="false"
              />
            </div>
          </div>

          <!-- Dynamic Countdown Overlay -->
          <div class="countdown-overlay" v-if="posterLoaded">
            <div class="countdown-number">{{ daysUntilEvent }}</div>
          </div>

          <!-- Dynamic Text Overlays -->
          <div class="text-overlays" v-if="posterLoaded">
            <!-- Draggable Name -->
            <div
              class="user-name draggable-text"
              :style="nameStyle"
              @mousedown="startTextDrag($event, 'name')"
              @touchstart="startTextTouch($event, 'name')"
            >
              {{ displayName }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="uploadedImage" class="device-instructions">
        <div class="desktop-instructions">
          <strong>Desktop:</strong> Click and drag the rectangular frame area to
          reposition • Scroll wheel over image to zoom in/out • Click and drag
          your name, location, or church text to reposition
        </div>
        <div class="mobile-instructions">
          <strong>Mobile:</strong> Touch and drag the rectangular frame to move
          • Pinch with two fingers over image to zoom • Touch and drag your
          name, location, or church text to reposition
        </div>
      </div>

      <div v-if="!uploadedImage" class="instructions">
        Click "Upload Image" to add your image to the poster. You can also drag
        your name, location, and church text to reposition them on the poster.
      </div>
    </div>
  </div>
</template>

<script>
import { saveAs } from "file-saver";
import { removeBackground } from "@imgly/background-removal";
import posterImage from "@/assets/Automation (1).png";
import logoImage from "@/assets/lutheran.png";

export default {
  name: "PosterEditor",
  data() {
    return {
      uploadedImage: null,
      posterTemplate: posterImage,
      logo: logoImage,
      posterLoaded: false,
      imageLoaded: false,
      imageContainerStyle: {},
      imageTransform: {
        x: 0,
        y: 0,
        scale: 1,
      },
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      lastPinchDistance: 0,
      isTouch: false,
      touchStartTime: 0,
      touchStartPosition: { x: 0, y: 0 },
      userName: "",
      isRemovingBg: false,
      // Text position tracking
      namePosition: { x: 10, y: 72.7 }, // Initial position as percentages (from top)
      isDraggingText: false,
      draggedElement: null, // 'name', 'location', or 'church'
      textDragStart: { x: 0, y: 0 },
    };
  },
  methods: {
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (
        file &&
        (file.type === "image/png" ||
          file.type === "image/jpeg" ||
          file.type === "image/jpg")
      ) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.uploadedImage = e.target.result;
          this.resetImagePosition();
        };
        reader.readAsDataURL(file);
      } else {
        alert("Please select a PNG or JPG image file.");
      }
      // Reset the input
      event.target.value = "";
    },

    onPosterLoaded() {
      this.posterLoaded = true;
      this.calculateImagePosition();
    },

    onImageLoaded() {
      this.imageLoaded = true;
      this.calculateImagePosition();
    },

    calculateImagePosition() {
      if (!this.posterLoaded) return;

      const posterContainer = this.$refs.posterContainer;
      if (!posterContainer) return;

      // The image interaction area position is now handled by computed property
      // This method now just sets up the internal image container
      this.imageContainerStyle = {
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        // Rectangular frame shape for the new poster design
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      };
    },

    async downloadPoster() {
      if (!this.posterLoaded) {
        alert("Please wait for the poster template to load.");
        return;
      }

      if (!this.uploadedImage) {
        alert("Please upload an image first.");
        return;
      }

      if (!this.userName.trim()) {
        alert("Please enter your name before downloading.");
        return;
      }

      try {
        const posterContainer = this.$refs.posterContainer;
        const posterImgEl = posterContainer.querySelector(
          "img.poster-template",
        );
        const containerWidth = posterContainer.offsetWidth;
        const containerHeight = posterContainer.offsetHeight;
        const naturalWidth = posterImgEl.naturalWidth;
        const naturalHeight = posterImgEl.naturalHeight;

        if (!naturalWidth || !naturalHeight) {
          throw new Error("Poster template dimensions unavailable.");
        }

        // Scale factors: from CSS display pixels to poster's native pixel space
        const scaleX = naturalWidth / containerWidth;
        const scaleY = naturalHeight / containerHeight;

        // Create canvas at the poster's native resolution
        const canvas = document.createElement("canvas");
        canvas.width = naturalWidth;
        canvas.height = naturalHeight;
        const ctx = canvas.getContext("2d");

        // White background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, naturalWidth, naturalHeight);

        // Draw poster template at full native resolution
        ctx.drawImage(posterImgEl, 0, 0, naturalWidth, naturalHeight);

        // Frame position — read from the CSS custom properties so both
        // the live preview and the downloaded image always stay in sync.
        const cs = getComputedStyle(posterContainer);
        const pct = (prop, fallback) => {
          const v = parseFloat(cs.getPropertyValue(prop));
          return (isNaN(v) ? fallback : v) / 100;
        };
        const frameLeft = naturalWidth * pct("--frame-left", 9.4);
        const frameTop = naturalHeight * pct("--frame-top", 43.4);
        const frameWidth = naturalWidth * pct("--frame-width", 22.8);
        const frameHeight = naturalHeight * pct("--frame-height", 22.8);

        // Load the uploaded user photo (base64 data URL — no CORS concern)
        const userImg = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = this.uploadedImage;
        });

        ctx.save();

        // Clip drawing to the frame rectangle
        ctx.beginPath();
        ctx.rect(frameLeft, frameTop, frameWidth, frameHeight);
        ctx.clip();

        // Reproduce objectFit: contain — match the CSS display exactly
        const imgAspect = userImg.naturalWidth / userImg.naturalHeight;
        const frameAspect = frameWidth / frameHeight;
        let drawW, drawH;
        if (imgAspect > frameAspect) {
          // Image wider than frame — fit by width, letterbox top/bottom
          drawW = frameWidth;
          drawH = drawW / imgAspect;
        } else {
          // Image taller than frame — fit by height, pillarbox left/right
          drawH = frameHeight;
          drawW = drawH * imgAspect;
        }

        // Apply the user's drag / zoom transform (CSS px → canvas px)
        const cx = frameLeft + frameWidth / 2;
        const cy = frameTop + frameHeight / 2;
        const tx = this.imageTransform.x * scaleX;
        const ty = this.imageTransform.y * scaleY;
        const s = this.imageTransform.scale;

        ctx.translate(cx + tx, cy + ty);
        ctx.scale(s, s);
        ctx.drawImage(userImg, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();

        // Draw text overlays — reads font size from live DOM, colour from parameter
        const drawText = (selector, text, posX, posY, color, textTransform = "uppercase") => {
          const el = posterContainer.querySelector(selector);
          if (!el || !text) return;

          const cs = window.getComputedStyle(el);
          const fontSizePx = parseFloat(cs.fontSize);
          const fontWeight = cs.fontWeight || "700";
          const fontSizeCanvas = fontSizePx * scaleX;

          const x = naturalWidth * (posX / 100);
          const y = naturalHeight * (posY / 100);

          ctx.save();
          ctx.font = `${fontWeight} ${fontSizeCanvas}px Montserrat, Poppins, Inter, sans-serif`;
          ctx.fillStyle = color;
          ctx.textAlign = "left";
          ctx.textBaseline = "top";
          ctx.shadowColor = "rgba(0,0,0,0.2)";
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 1;
          ctx.shadowBlur = 2;
          const drawn = textTransform === "uppercase" ? text.toUpperCase() : text;
          ctx.fillText(drawn, x, y);
          ctx.restore();
        };

        // Draw user name — use the same colour as the CSS .user-name
        drawText(
          ".user-name",
          this.displayName,
          this.namePosition.x,
          this.namePosition.y,
          "#dce8e0",
        );

        // Draw countdown number — measure the number element itself for pixel-perfect alignment
        const countdownEl = posterContainer.querySelector(".countdown-number");
        if (countdownEl) {
          const containerRect = posterContainer.getBoundingClientRect();
          const numRect = countdownEl.getBoundingClientRect();
          // Horizontal centre + vertical centre of the element — matches textBaseline:"middle"
          const cx = ((numRect.left + numRect.width / 2 - containerRect.left) / containerRect.width) * naturalWidth;
          const cy = ((numRect.top + numRect.height / 2 - containerRect.top) / containerRect.height) * naturalHeight;
          const cs = window.getComputedStyle(countdownEl);
          const fontSizeCanvas = parseFloat(cs.fontSize) * scaleX;
          ctx.save();
          ctx.font = `900 ${fontSizeCanvas}px Arial Black, Arial, sans-serif`;
          ctx.fillStyle = "#1565c0";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(this.daysUntilEvent), cx, cy);
          ctx.restore();
        }

        // Save as PNG
        canvas.toBlob(
          (blob) => {
            if (blob) {
              saveAs(blob, "kongamano-poster.png");
            } else {
              const link = document.createElement("a");
              link.download = "kongamano-poster.png";
              link.href = canvas.toDataURL("image/png", 1.0);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          },
          "image/png",
          1.0,
        );
      } catch (error) {
        console.error("Error generating poster:", error);
        alert("Error generating poster. Please try again.");
      }
    },

    startDrag(event) {
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX - this.imageTransform.x,
        y: event.clientY - this.imageTransform.y,
      };
      event.preventDefault();
      event.stopPropagation();
    },

    handleDrag(event) {
      if (!this.isDragging) return;

      this.imageTransform.x = event.clientX - this.dragStart.x;
      this.imageTransform.y = event.clientY - this.dragStart.y;
      event.preventDefault();
      event.stopPropagation();
    },

    stopDrag() {
      this.isDragging = false;
    },

    handleWheel(event) {
      if (!this.uploadedImage) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // More sensitive scaling for better control
      const delta = event.deltaY > 0 ? -0.05 : 0.05;
      const newScale = this.imageTransform.scale + delta;

      // Update scale with smooth transition
      this.imageTransform.scale = Math.max(0.3, Math.min(5, newScale));

      // Force reactivity update
      this.$forceUpdate();
    },

    startTouch(event) {
      this.isTouch = true;
      event.preventDefault();
      event.stopPropagation();

      // Stop any ongoing drag when starting new touch
      this.isDragging = false;

      if (event.touches.length === 1) {
        // Single touch - start drag immediately
        this.isDragging = true;
        this.dragStart = {
          x: event.touches[0].clientX - this.imageTransform.x,
          y: event.touches[0].clientY - this.imageTransform.y,
        };
      } else if (event.touches.length === 2) {
        // Two touches - start pinch zoom
        this.isDragging = false; // Stop dragging when pinching
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        this.lastPinchDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );
      }
    },

    handleTouch(event) {
      if (!this.isTouch) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (event.touches.length === 1 && this.isDragging) {
        // Single touch drag
        this.imageTransform.x = event.touches[0].clientX - this.dragStart.x;
        this.imageTransform.y = event.touches[0].clientY - this.dragStart.y;
      } else if (event.touches.length === 2) {
        // Two touch pinch to zoom
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );

        if (this.lastPinchDistance > 0) {
          const scaleChange = currentDistance / this.lastPinchDistance;
          const newScale = this.imageTransform.scale * scaleChange;

          // Apply scale with better bounds and sensitivity
          this.imageTransform.scale = Math.max(0.3, Math.min(5, newScale));

          // Force reactivity update
          this.$forceUpdate();
        }

        this.lastPinchDistance = currentDistance;
      }
    },

    stopTouch() {
      this.isDragging = false;
      this.isTouch = false;
      this.lastPinchDistance = 0;
    },

    resetImagePosition() {
      // Center the image within its circular container
      this.imageTransform = {
        x: 0,
        y: 0,
        scale: 1.2, // Start slightly zoomed to fill the circular area better
      };

      // Force reactivity update
      this.$forceUpdate();
    },

    async removeBg() {
      if (!this.uploadedImage || this.isRemovingBg) return;
      this.isRemovingBg = true;
      try {
        const blob = await removeBackground(this.uploadedImage);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.uploadedImage = e.target.result;
          this.isRemovingBg = false;
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error("Background removal failed:", err);
        alert("Background removal failed. Please try again.");
        this.isRemovingBg = false;
      }
    },

    // Text dragging methods
    startTextDrag(event, element) {
      event.preventDefault();
      event.stopPropagation();

      this.isDraggingText = true;
      this.draggedElement = element;

      const posterContainer = this.$refs.posterContainer;
      const rect = posterContainer.getBoundingClientRect();

      // Calculate relative position within poster
      const relativeX = ((event.clientX - rect.left) / rect.width) * 100;
      const relativeY = ((event.clientY - rect.top) / rect.height) * 100;

      // Store offset from current position
      const currentPos = this.namePosition;
      this.textDragStart = {
        x: relativeX - currentPos.x,
        y: relativeY - currentPos.y,
      };
    },

    handleTextDrag(event) {
      if (!this.isDraggingText || !this.draggedElement) return;

      event.preventDefault();
      event.stopPropagation();

      const posterContainer = this.$refs.posterContainer;
      const rect = posterContainer.getBoundingClientRect();

      // Calculate new position as percentage
      const relativeX = ((event.clientX - rect.left) / rect.width) * 100;
      const relativeY = ((event.clientY - rect.top) / rect.height) * 100;

      const newX = relativeX - this.textDragStart.x;
      const newY = relativeY - this.textDragStart.y;

      // Constrain to poster bounds (with some padding)
      const constrainedX = Math.max(0, Math.min(65, newX)); // Max 65% to prevent overflow
      const constrainedY = Math.max(0, Math.min(90, newY)); // Max 90% to prevent overflow

      if (this.draggedElement === "name") {
        this.namePosition = { x: constrainedX, y: constrainedY };
      }
    },

    stopTextDrag() {
      this.isDraggingText = false;
      this.draggedElement = null;
    },

    // Touch support for text dragging
    startTextTouch(event, element) {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        const syntheticEvent = {
          clientX: touch.clientX,
          clientY: touch.clientY,
          preventDefault: () => event.preventDefault(),
          stopPropagation: () => event.stopPropagation(),
        };
        this.startTextDrag(syntheticEvent, element);
      }
    },

    handleTextTouch(event) {
      if (event.touches.length === 1 && this.isDraggingText) {
        const touch = event.touches[0];
        const syntheticEvent = {
          clientX: touch.clientX,
          clientY: touch.clientY,
          preventDefault: () => event.preventDefault(),
          stopPropagation: () => event.stopPropagation(),
        };
        this.handleTextDrag(syntheticEvent);
      }
    },
  },

  computed: {
    uploadedImageStyle() {
      if (!this.uploadedImage) return {};

      return {
        width: "100%",
        height: "100%",
        transform: `translate(${this.imageTransform.x}px, ${this.imageTransform.y}px) scale(${this.imageTransform.scale})`,
        transformOrigin: "center center",
        transition: this.isDragging ? "none" : "transform 0.1s ease-out",
        objectFit: "contain",
        maxWidth: "none",
        maxHeight: "none",
        imageRendering: "auto",
        pointerEvents: "none", // Prevent conflicts
      };
    },

    imageInteractionStyle() {
      if (!this.uploadedImage) return {};

      // Positions come from the CSS custom properties on .poster-container.
      // Adjust --frame-left/top/width/height in the <style> block to reposition.
      return {
        position: "absolute",
        left: "var(--frame-left)",
        top: "var(--frame-top)",
        width: "var(--frame-width)",
        height: "var(--frame-height)",
        zIndex: 10,
        cursor: this.isDragging ? "grabbing" : "grab",
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        overflow: "hidden",
      };
    },

    displayName() {
      return this.userName.trim() || "JINA LAKO";
    },

    canDownload() {
      return this.uploadedImage && this.userName.trim();
    },

    daysUntilEvent() {
      const eventDate = new Date("2026-08-05");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    },

    // Dynamic font size for user name based on length
    nameStyle() {
      const name = this.displayName;
      const words = name.split(" ").filter((word) => word.trim().length > 0); // Remove empty strings
      const wordCount = words.length;
      const contentLength = words.join("").length; // Length without spaces

      let fontSize;
      let lineHeight;
      let whiteSpace;
      let wordBreak;

      // More intelligent line breaking logic
      if (wordCount <= 2) {
        // 1-2 words: Keep on one line unless very long
        if (contentLength <= 18) {
          fontSize = "2.2vw";
          lineHeight = "1.1";
          whiteSpace = "nowrap";
          wordBreak = "keep-all";
        } else {
          // Very long single/two words - allow breaking
          fontSize = "2.0vw";
          lineHeight = "1.0";
          whiteSpace = "normal";
          wordBreak = "break-word";
        }
      } else if (wordCount === 3 && contentLength <= 18) {
        // 3 short words: Try to keep on one line with smaller font
        fontSize = "1.9vw";
        lineHeight = "1.0";
        whiteSpace = "nowrap";
        wordBreak = "normal";
      } else {
        // Long names or 4+ words: Allow line breaks with good font size
        fontSize = "2.0vw";
        lineHeight = "0.95";
        whiteSpace = "normal";
        wordBreak = "break-word";
      }

      return {
        position: "absolute",
        left: `${this.namePosition.x}%`,
        top: `${this.namePosition.y}%`,
        fontSize: fontSize,
        lineHeight: lineHeight,
        wordSpacing: wordCount > 3 ? "-0.1em" : "normal",
        wordBreak: wordBreak,
        whiteSpace: whiteSpace,
        cursor:
          this.isDraggingText && this.draggedElement === "name"
            ? "grabbing"
            : "grab",
        userSelect: "none",
        zIndex:
          this.isDraggingText && this.draggedElement === "name" ? 1000 : 5,
        transition: this.isDraggingText ? "none" : "all 0.2s ease",
      };
    },
  },

  mounted() {
    // Recalculate position on window resize for responsiveness
    window.addEventListener("resize", this.calculateImagePosition);
    // Add mouse and touch event listeners for image drag
    document.addEventListener("mousemove", this.handleDrag);
    document.addEventListener("mouseup", this.stopDrag);
    document.addEventListener("touchend", this.stopTouch);

    // Add event listeners for text dragging
    document.addEventListener("mousemove", this.handleTextDrag);
    document.addEventListener("mouseup", this.stopTextDrag);
    document.addEventListener("touchmove", this.handleTextTouch);
    document.addEventListener("touchend", this.stopTextDrag);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.calculateImagePosition);
    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("touchend", this.stopTouch);

    // Remove text dragging event listeners
    document.removeEventListener("mousemove", this.handleTextDrag);
    document.removeEventListener("mouseup", this.stopTextDrag);
    document.removeEventListener("touchmove", this.handleTextTouch);
    document.removeEventListener("touchend", this.stopTextDrag);
  },
};
</script>

<style scoped>
.poster-editor {
  min-height: 100vh;
  background-color: white;
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}

.header {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
  color: white;
  padding: 2rem 0;
  box-shadow: 0 4px 20px rgba(13, 71, 161, 0.4);
  border-bottom: 4px solid #f5a623;
  position: relative;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 0 2rem;
}

.logo-container {
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.header-logo {
  height: 60px;
  width: auto;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4)) brightness(1.1)
    contrast(1.1);
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 0.5rem;
}

.header-logo:hover {
  transform: scale(1.05);
  background: rgba(255, 255, 255, 1);
  filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.5)) brightness(1.2)
    contrast(1.2);
}

.logo-container:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.header-text {
  flex: 1;
  text-align: left;
}

.header h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2.5rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.5px;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-subtitle {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 400;
  color: #f5a623;
  opacity: 0.95;
  letter-spacing: 0.5px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.controls {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2rem;
  align-items: center;
}

.user-inputs {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 320px; /* Increased from 250px for consistency with expanded countries dropdown */
}

.input-group label {
  font-weight: bold;
  color: #333;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.user-input,
.user-select {
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  background: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}

.user-input:focus,
.user-select:focus {
  outline: none;
  border-color: #1565c0;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15);
  transform: translateY(-1px);
}

.user-input::placeholder {
  color: #999;
  font-style: italic;
}

.user-select {
  cursor: pointer;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.user-select:hover {
  border-color: #27ae60;
}

.user-select option {
  padding: 8px 12px;
  font-weight: 500;
  color: #333;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.upload-btn {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 8px 25px rgba(21, 101, 192, 0.4);
  position: relative;
  overflow: hidden;
}

.upload-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.6s;
}

.upload-btn:hover {
  transform: translateY(-6px) scale(1.05);
  box-shadow: 0 15px 35px rgba(21, 101, 192, 0.6);
}

.upload-btn:hover::before {
  left: 100%;
}

.upload-btn:active {
  transform: translateY(-2px) scale(1.02);
}

.download-btn {
  background: linear-gradient(135deg, #f5a623 0%, #e08c0a 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 8px 25px rgba(245, 166, 35, 0.4);
  position: relative;
  overflow: hidden;
}

.download-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.6s;
}

.download-btn:hover:not(:disabled) {
  transform: translateY(-6px) scale(1.05);
  box-shadow: 0 15px 35px rgba(245, 166, 35, 0.6);
}

.download-btn:hover:not(:disabled)::before {
  left: 100%;
}

.download-btn:active:not(:disabled) {
  transform: translateY(-2px) scale(1.02);
}

.download-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #ccc;
}

.reset-btn {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.4);
  position: relative;
  overflow: hidden;
}

.reset-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.6s;
}

.reset-btn:hover:not(:disabled) {
  transform: translateY(-6px) scale(1.05);
  box-shadow: 0 15px 35px rgba(25, 118, 210, 0.6);
}

.reset-btn:hover:not(:disabled)::before {
  left: 100%;
}

.reset-btn:active:not(:disabled) {
  transform: translateY(-2px) scale(1.02);
}

.reset-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #ccc;
}

.poster-preview {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.poster-container {
  position: relative;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  /* ── Photo frame position ──────────────────────────────────────
     Adjust ONLY these four values to reposition / resize the
     uploaded-image slot on the poster.  No JS changes needed.

     --frame-left   : left edge of the slot  (% of poster width)
     --frame-top    : top edge of the slot   (% of poster height)
     --frame-width  : slot width             (% of poster width)
     --frame-height : slot height            (% of poster height)
  ─────────────────────────────────────────────────────────────── */
  --frame-left: 7.4%;
  --frame-top: 33.9%;
  --frame-width: 33.8%;
  --frame-height: 35.8%;
}

.poster-template {
  width: 100%;
  height: auto;
  display: block;
  position: relative;
  z-index: 1; /* Base layer */
}

.image-interaction-area {
  /* This is the interactive overlay area where users can drag/scale the image */
  /* Positioned via computed style imageInteractionStyle */
  touch-action: none; /* Prevent browser zoom/pan */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  transition: all 0.3s ease;
  /* Add a subtle backdrop filter for better visual separation */
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

.image-interaction-area:hover {
  background: rgba(39, 174, 96, 0.08);
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
  box-shadow: inset 0 0 0 2px rgba(39, 174, 96, 0.35),
    0 4px 12px rgba(39, 174, 96, 0.15);
  transform: scale(1.02);
}

.image-interaction-area:active {
  background: rgba(39, 174, 96, 0.12);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  box-shadow: inset 0 0 0 3px rgba(39, 174, 96, 0.5),
    0 2px 8px rgba(39, 174, 96, 0.2);
  transform: scale(1.01);
}

.uploaded-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* Rectangular frame for the new poster design */
  border-radius: 12px; /* Rounded corners for the frame */
  /* Transparent background - no visible empty spaces */
  background: transparent;
}

.uploaded-image {
  position: absolute;
  top: 0;
  left: 0;
  image-rendering: auto; /* Better for scaling */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.device-instructions {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  padding: 1rem;
  margin-top: 1rem;
  background: #e8f0fe;
  border-radius: 6px;
  border-left: 3px solid #1565c0;
  line-height: 1.6;
}

.desktop-instructions,
.mobile-instructions {
  margin: 0.5rem 0;
}

.desktop-instructions strong,
.mobile-instructions strong {
  color: #333;
}

.instructions {
  text-align: center;
  color: #444;
  font-size: 1.1rem;
  padding: 2rem;
  background: #e8f0fe;
  border-radius: 8px;
  border-left: 4px solid #1565c0;
}

/* Text Overlay Styles */
.text-overlays {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3; /* Above the poster template */
  pointer-events: none; /* Allow interactions to pass through, but children can override */
}

/* Draggable Text Elements */
.draggable-text {
  pointer-events: auto; /* Enable interactions for draggable text */
  text-align: center;
  max-width: 50%; /* Increased from 35% to allow more space */
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: inline-block; /* Allows text to wrap naturally while maintaining center alignment */
}

.draggable-text:hover {
  background: rgba(39, 174, 96, 0.15);
  border-radius: 4px;
  padding: 2px 6px;
  box-shadow: 0 2px 8px rgba(39, 174, 96, 0.25);
}

.draggable-text:active {
  background: rgba(39, 174, 96, 0.25);
  transform: scale(1.02);
}

.user-name {
  font-family: "Montserrat", "Poppins", "Inter", "Segoe UI", "Roboto",
    sans-serif;
  font-weight: 700;
  color: #dce8e0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.4);
}

/* Remove Background Button */
.removebg-btn {
  background: linear-gradient(135deg, #0d47a1 0%, #082f6b 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 8px 25px rgba(13, 71, 161, 0.4);
}

.removebg-btn:hover:not(:disabled) {
  transform: translateY(-6px) scale(1.05);
  box-shadow: 0 15px 35px rgba(13, 71, 161, 0.6);
}

.removebg-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Countdown Overlay Styles */
.countdown-overlay {
  position: absolute;
  left: 52%;
  top: 62.4%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12%;
}

.countdown-number {
  font-family: "Arial Black", Arial, sans-serif;
  font-size: 8vw;
  font-weight: 900;
  color: #1565c0;
  line-height: 1;
  text-align: center;
}

/* Desktop-only instructions */
@media (min-width: 769px) {
  .mobile-instructions {
    display: none; /* Hide mobile instructions on desktop */
  }
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .header {
    padding: 1.5rem 0;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
    padding: 0 1rem;
  }

  .header-text {
    text-align: center;
  }

  .header h1 {
    font-size: 2rem;
  }

  .header-subtitle {
    font-size: 1rem;
  }

  .header-logo {
    height: 50px;
  }

  .logo-container {
    padding: 0.8rem;
  }

  .container {
    padding: 1rem;
  }

  .controls {
    flex-direction: column;
    align-items: center;
  }

  .upload-btn,
  .download-btn,
  .reset-btn,
  .removebg-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
    padding: 16px 24px;
    font-size: 1.1rem;
  }

  .poster-container {
    margin: 0 -1rem;
    width: calc(100% + 2rem);
  }

  .instructions {
    margin: 0 -1rem;
    border-radius: 0;
  }

  .device-instructions {
    margin: 1rem -1rem 0 -1rem;
    border-radius: 0;
    font-size: 0.85rem;
  }

  .desktop-instructions {
    display: none; /* Hide desktop instructions on mobile */
  }

  .countdown-number {
    font-size: 8vw; /* Slightly larger on mobile */
  }

  .draggable-text {
    max-width: 50%; /* Even wider on mobile for better text flow */
  }

  /* Better responsive font sizing for mobile devices */
  .user-name {
    /* Smaller font size for mobile to prevent overwhelming the poster */
    font-size: clamp(10px, 1.8vw, 2.5vw) !important;
    line-height: 1.1 !important;
  }

  .input-group {
    min-width: 280px; /* Increased from 200px for better consistency on mobile */
  }

  .user-inputs {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  /* Force consistent width for all input elements on mobile */
  .user-input,
  .user-select {
    width: 100% !important;
    max-width: 350px;
    box-sizing: border-box;
  }
}

@media (max-width: 480px) {
  .header {
    padding: 1rem 0;
  }

  .header h1 {
    font-size: 1.5rem;
  }

  .header-subtitle {
    font-size: 0.9rem;
  }

  .header-logo {
    height: 40px;
  }

  .logo-container {
    padding: 0.6rem;
  }

  .header-content {
    padding: 0 1rem;
    gap: 1rem;
  }

  .container {
    padding: 0.5rem;
  }

  /* Even smaller font sizes for very small mobile screens */
  .user-name {
    font-size: clamp(8px, 1.5vw, 2vw) !important;
  }

  /* Ensure consistent input widths on very small screens */
  .input-group {
    min-width: 100%;
    width: 100%;
  }

  .user-input,
  .user-select {
    width: 100% !important;
    max-width: 320px;
    min-width: 280px;
  }
}
</style>
